import Vue from '../../../assets/vue';
import * as cartApi from '../../../scripts/cartApi';
import {
  cacheCart,
  getState,
  removeItem,
  updateItems,
} from '../../../scripts/cartApi';
import {
  calcPreOrderMessage,
  calcBundlePreOrderMessage,
  formatPriceWithoutZeros,
} from '../../../scripts/utils/product-helpers';
import { setMaxHeightStyle } from '../../../scripts/utils/setMaxHeightStyle';
import { sendCustomEvent } from '../../../scripts/config/analytics';
import { PRODUCT_TYPE } from '../../../scripts/config/constants';
import { calcBogoBonusProducts } from '../../../scripts/utils/bogo-helpers';

const UPDATING_ATTR_NAME = 'data-updating';
const HIDE_ATTR_NAME = 'data-hide';
const HEADER_CART_COUNT_ATTR = 'data-site-header-controls-cart-count';
const CART_COUNT_ATTR = 'data-menu-cart-count';
const CART_COUNT_ANIMATE_ATTR = 'data-menu-cart-count-animate';
const CART_LINE_KEY_ATTR = 'data-side-cart-line-key';
const COLLAPSIBLE_ANIMATION_DURATION = 250;

const { cartConfig } = window;

const startCartCountAnimation = () => {
  const cartCountEl = document.querySelector(`[${CART_COUNT_ATTR}]`);

  cartCountEl.setAttribute(CART_COUNT_ANIMATE_ATTR, 'false');

  // NOTE: Reflow dom
  setTimeout(() => {
    cartCountEl.setAttribute(CART_COUNT_ANIMATE_ATTR, 'true');
  }, 10);
};

const updateCountOfProductsInCart = (count) => {
  const cartCountEl = document.querySelector(`[${CART_COUNT_ATTR}]`);
  const headerControlsEl = document.querySelector(
    '[data-site-header-controls]'
  );

  if (count) {
    headerControlsEl.setAttribute(HEADER_CART_COUNT_ATTR, String(count));
    cartCountEl.setAttribute(CART_COUNT_ATTR, count);
    startCartCountAnimation();
  } else {
    getState().then((cart) => {
      headerControlsEl.setAttribute(
        HEADER_CART_COUNT_ATTR,
        String(cart.item_count)
      );
      cartCountEl.setAttribute(CART_COUNT_ATTR, count);
      cacheCart(cart);
      startCartCountAnimation();
    });
  }
};

export const openSideCart = () => {
  const { DeviceHelper } = window;

  if (!DeviceHelper?.isTabletViewport()) {
    window.drawers.openDrawer('right');
    sendCustomEvent({
      category: 'Cart',
      action: 'Open',
      label: 'After Added Product',
    });
  }
};

export const closeSideCart = () => {
  window.drawers.closeDrawer('right');
};

const toggleAttr = (selector, attrName) => {
  const productLineEl = document.querySelector(selector);

  if (productLineEl) {
    productLineEl.toggleAttribute(attrName);
  }
};

const findLineItemByKey = (lineItems, key) => {
  if (lineItems) {
    return lineItems.find((lineItem) => lineItem.key === key);
  }

  return null;
};

const getBundleLineItems = (cart) => {
  const allStarterBundles = cart.items.filter(
    (item) => item.properties?.Bundle
  );

  const bundleLinesObj = allStarterBundles.reduce((acc, item) => {
    if (!acc[item.properties.Bundle]) {
      acc[item.properties.Bundle] = [item];
    } else {
      acc[item.properties.Bundle].push(item);
    }
    return acc;
  }, {});

  return Object.entries(bundleLinesObj).map(([Bundle, items]) => ({
    items,
    Bundle,
  }));
};

const getSimpleLineItems = (cart) =>
  cart.items.filter((item) => !item.properties?.Bundle);

const calcVariantMainDataByCartConfig = (variantId) =>
  cartConfig.variantsMainData[variantId] || {};

const calcVariantMetaByCartConfig = (variantId) =>
  cartConfig.variantsMeta[variantId] || {};

const calcBundleConfigByCartConfig = (variantId) =>
  cartConfig.bundleConfig[variantId] || {};

const calcTotalSavinMoney = (lineItems, cartTotalDiscount) => {
  const savingMoneyFromComparedAtPriceArray = lineItems.map((lineItem) => {
    const variantMainData = calcVariantMainDataByCartConfig(
      lineItem.variant_id
    );

    if (variantMainData?.compareAtPrice) {
      return (
        (variantMainData?.compareAtPrice - lineItem.price) * lineItem.quantity
      );
    }

    return 0;
  });

  const totalSavingComparedAtPrice = savingMoneyFromComparedAtPriceArray.reduce(
    (sum, savingMoneyFromComparedAtPrice) =>
      sum + savingMoneyFromComparedAtPrice,
    0
  );

  return totalSavingComparedAtPrice + cartTotalDiscount;
};

const hasLineItemBogoDiscount = (lineItemDiscounts) =>
  lineItemDiscounts.length && lineItemDiscounts[0].amount === 0;

const SideCart = {
  data() {
    this.cart = cacheCart();
    this.simpleLineItems = getSimpleLineItems(this.cart);
    this.bundleLineItems = getBundleLineItems(this.cart);

    return {
      isCartEmpty: this.cart.items.length === 0,
      totalPrice: this.cart.total_price,
      totalSavinMoney: calcTotalSavinMoney(
        this.simpleLineItems,
        this.cart.total_discount
      ),
      isMounted: false,
    };
  },
  computed: {
    totalDiscountMessage() {
      const formattedTotalSavingPrice = formatPriceWithoutZeros(
        this.totalSavinMoney
      );

      return `You're saving ${formattedTotalSavingPrice}`;
    },
    totalCartPrice() {
      return formatPriceWithoutZeros(this.totalPrice);
    },
  },
  methods: {
    updateState(cart) {
      this.cart = cart;
      this.simpleLineItems = getSimpleLineItems(this.cart);
      this.bundleLineItems = getBundleLineItems(this.cart);
      this.totalPrice = cart.total_price;
      this.isCartEmpty = cart.items.length === 0;
      this.totalSavinMoney = calcTotalSavinMoney(
        this.simpleLineItems,
        this.cart.total_discount
      );
    },
    updateView(cart = null, cb) {
      const useCart = (_cart) => {
        cacheCart(_cart);
        updateCountOfProductsInCart(_cart.item_count);
        this.updateState(_cart);
        this.$forceUpdate();
        if (typeof cb === 'function') {
          cb();
        }
      };

      if (cart) {
        useCart(cart);
      } else {
        getState().then((newCart) => {
          useCart(newCart);
        });
      }
    },
    calcProductTitle(lineItemKey) {
      const lineItem = this.getSimpleLineItemByKey(lineItemKey);

      if (
        lineItem.product_type === PRODUCT_TYPE.CONTROLS ||
        lineItem.product_type === PRODUCT_TYPE.REFURBISHED_CONTROLS
      ) {
        return `${lineItem?.product_title}`;
      }

      return `${lineItem?.title}`;
    },
    calcDiscountText(discounts) {
      if (discounts.length === 0) {
        return '';
      }

      // NOTE: For the BOGO promo product
      if (discounts[0].amount === 0) {
        return `${discounts[0].title}`;
      }

      return `${discounts[0].title} (-${formatPriceWithoutZeros(
        discounts[0].amount
      )})`;
    },
    calcBundleDiscountText(bundleKey) {
      const bundleLineItems = this.getBundleLineItemById(bundleKey);
      const discounts = bundleLineItems
        .map((bundleLineItem) => bundleLineItem.discounts[0])
        .filter(Boolean);

      if (!discounts.length) {
        return '';
      }

      const discountPrice = discounts.reduce(
        (sum, discount) => sum + discount?.amount,
        0
      );

      return `${discounts[0].title} (-${formatPriceWithoutZeros(
        discountPrice
      )})`;
    },
    removeProductFromCart(lineItemKey) {
      const productLineSelector = `[${CART_LINE_KEY_ATTR}='${lineItemKey}']`;

      setMaxHeightStyle(productLineSelector);
      toggleAttr(productLineSelector, UPDATING_ATTR_NAME);

      removeItem(lineItemKey).then((newCart) => {
        const hasRemovedLine = findLineItemByKey(newCart.items, lineItemKey);

        toggleAttr(
          productLineSelector,
          hasRemovedLine ? UPDATING_ATTR_NAME : HIDE_ATTR_NAME
        );

        setTimeout(() => {
          this.updateView(newCart);
        }, COLLAPSIBLE_ANIMATION_DURATION);

        const lineItem = this.getSimpleLineItemByKey(lineItemKey);

        sendCustomEvent({
          category: 'Cart',
          action: 'Remove',
          label: lineItem.variant_id,
        });
      });
    },
    removeBundleFromCart(bundleKey) {
      const productLineSelector = `[${CART_LINE_KEY_ATTR}='${bundleKey}']`;

      setMaxHeightStyle(productLineSelector);

      toggleAttr(productLineSelector, UPDATING_ATTR_NAME);

      const bundleLineItems = this.getBundleLineItemById(bundleKey);

      const lineBundleConfig = Object.values(cartConfig.bundleConfig).find(
        (bundleConfigData) => bundleConfigData?.property_value === bundleKey
      );
      const updatedBundleLineItems = bundleLineItems.map((bundleLineItem) => {
        const defaultQuantity =
          lineBundleConfig?.variants_quantity[
            lineBundleConfig.variants.findIndex(
              ({ id }) => id === bundleLineItem.id
            )
          ] || bundleLineItem.quantity;

        return {
          ...bundleLineItem,
          quantity: bundleLineItem.quantity - defaultQuantity,
        };
      });

      updateItems(updatedBundleLineItems).then((newCart) => {
        const hasRemovedLine = updatedBundleLineItems.every(
          (item) => item.quantity !== 0
        );

        toggleAttr(
          productLineSelector,
          hasRemovedLine ? UPDATING_ATTR_NAME : HIDE_ATTR_NAME
        );

        setTimeout(() => {
          this.updateView(newCart);
        }, COLLAPSIBLE_ANIMATION_DURATION);

        sendCustomEvent({
          category: 'Bundle Behavior',
          action: 'Click',
          label: 'Removed from cart',
        });
      });
    },
    calcBogoBonusLineItems(lineItemVariantId) {
      const bogoBonusProducts = calcBogoBonusProducts(
        cartConfig.bogo[lineItemVariantId]
      );

      return bogoBonusProducts.map((bogoItem) =>
        this.getBogoBonusLineItemBuyVariantId(bogoItem.id)
      );
    },
    calcLineItemsForUpdate(lineItemKey) {
      const currentLineItem = this.getSimpleLineItemByKey(lineItemKey);

      if (hasLineItemBogoDiscount(currentLineItem.discounts)) {
        const bogoBonusLineItems = this.calcBogoBonusLineItems(
          currentLineItem.variant_id
        );

        return [currentLineItem, ...bogoBonusLineItems];
      }

      return [currentLineItem];
    },
    increaseQuantity(lineItemKey) {
      const items = this.calcLineItemsForUpdate(lineItemKey);
      const updateLineItems = items.map((item) => ({
        key: item.key,
        quantity: item.quantity + 1,
      }));

      updateLineItems.forEach((lineItem) => {
        const productLineSelector = `[${CART_LINE_KEY_ATTR}='${lineItem.key}']`;

        toggleAttr(productLineSelector, UPDATING_ATTR_NAME);
      });

      updateItems(updateLineItems).then((newCart) => {
        this.updateView(newCart);

        updateLineItems.forEach((lineItem) => {
          const productLineSelector = `[${CART_LINE_KEY_ATTR}='${lineItem.key}']`;

          toggleAttr(productLineSelector, UPDATING_ATTR_NAME);
          sendCustomEvent({
            category: 'Cart',
            action: 'Quantity increase',
            label: lineItem.variant_id,
          });
        });
      });
    },
    decreaseQuantity(lineItemKey) {
      const items = this.calcLineItemsForUpdate(lineItemKey);
      const updateLineItems = items.map((item) => ({
        key: item.key,
        quantity: item.quantity - 1,
      }));

      updateLineItems.forEach((lineItem) => {
        const productLineSelector = `[${CART_LINE_KEY_ATTR}='${lineItem.key}']`;

        toggleAttr(productLineSelector, UPDATING_ATTR_NAME);
      });

      updateItems(updateLineItems).then((newCart) => {
        this.updateView(newCart);

        updateLineItems.forEach((lineItem) => {
          const productLineSelector = `[${CART_LINE_KEY_ATTR}='${lineItem.key}']`;

          toggleAttr(productLineSelector, UPDATING_ATTR_NAME);
          sendCustomEvent({
            category: 'Cart',
            action: 'Quantity increase',
            label: lineItem.variant_id,
          });
        });
      });
    },
    calcLinePrice(finalLinePrice) {
      const formattedLinePrice = formatPriceWithoutZeros(finalLinePrice);

      return formattedLinePrice || 'Free';
    },
    calcCompareAtLinePrice(lineItem) {
      const variantMainData = calcVariantMainDataByCartConfig(
        lineItem.variant_id
      );
      const originalLineItemPrice = variantMainData?.compareAtPrice;

      if (originalLineItemPrice) {
        const totalOriginalLineItemPrice = originalLineItemPrice
          ? originalLineItemPrice * lineItem.quantity
          : 0;

        return totalOriginalLineItemPrice
          ? formatPriceWithoutZeros(totalOriginalLineItemPrice)
          : '';
      }

      return '';
    },
    showOriginalLinePrice(lineItem) {
      if (this.calcCompareAtLinePrice(lineItem)) {
        return this.calcCompareAtLinePrice(lineItem);
      }

      if (lineItem.original_price !== lineItem.final_price) {
        return formatPriceWithoutZeros(lineItem.original_line_price);
      }

      return '';
    },
    calcBundleFinalPrice(bundleKey) {
      const bundleLineItems = this.getBundleLineItemById(bundleKey);
      const bundleFinalPrice = bundleLineItems.reduce(
        (sum, bundleLineItem) => sum + bundleLineItem.final_line_price,
        0
      );

      return formatPriceWithoutZeros(bundleFinalPrice);
    },
    calcBundleOriginalPrice(bundleKey) {
      const bundleLineItems = this.getBundleLineItemById(bundleKey);
      const bundleOriginalPrice = bundleLineItems.reduce(
        (sum, bundleLineItem) => sum + bundleLineItem.original_line_price,
        0
      );

      return formatPriceWithoutZeros(bundleOriginalPrice);
    },
    calcBundleHeaderLabel(lineItems) {
      let bundleLabel = '';

      lineItems.some((lineItem) => {
        if (lineItem.product_type === 'Switch Pack Small') {
          bundleLabel = '3 PACK BUNDLE';
        } else if (lineItem.product_type === 'Switch Pack Medium') {
          bundleLabel = '10 PACK BUNDLE';
        } else if (lineItem.product_type === 'Switch Pack Large') {
          bundleLabel = '60 PACK BUNDLE';
        } else if (lineItem.product_type === 'Starter Bundle Controls') {
          bundleLabel = 'Starter Pack Bundle';
        } else if (lineItem.product_type === 'Closeout') {
          bundleLabel = 'Close Out';
        }
        return bundleLabel;
      });

      return bundleLabel;
    },
    calcSimpleLineInventoryMessage(variantId) {
      const variantMainData = calcVariantMainDataByCartConfig(variantId);
      const { preOrderCartText } = calcVariantMetaByCartConfig(variantId);
      const { inventoryQuantity, inventoryPolicy } = variantMainData;

      return calcPreOrderMessage(
        preOrderCartText,
        inventoryQuantity,
        inventoryPolicy
      );
    },
    calcBundleLineInventoryMessage(bundleLineItem) {
      const variantForPreviewId = Object.keys(cartConfig.bundleConfig).find(
        (variantId) =>
          calcBundleConfigByCartConfig(variantId).property_value ===
          bundleLineItem.Bundle
      );

      if (variantForPreviewId) {
        const { preOrderCartText } =
          calcVariantMetaByCartConfig(variantForPreviewId);

        return calcBundlePreOrderMessage(
          calcBundleConfigByCartConfig(variantForPreviewId),
          preOrderCartText
        );
      }

      return '';
    },
    closeCart() {
      closeSideCart();
    },
    getBundleLineItemById(bundleKey) {
      return this.bundleLineItems.find(({ Bundle }) => Bundle === bundleKey)
        .items;
    },
    getSimpleLineItemByKey(key) {
      return this.cart.items.find((item) => item.key === key);
    },
    getBogoBonusLineItemBuyVariantId(variantId) {
      return this.cart.items.find(
        (item) => item.variant_id === variantId && item.final_line_price === 0
      );
    },
  },
  mounted() {
    this.isMounted = true;
  },
};
const mountCart = () => new Vue(SideCart).$mount('#side-cart');

export const initCart = async () => {
  if (!window.sideCart) {
    const cart = await cartApi.getState();

    cacheCart(cart);
    window.sideCart = mountCart();
  }
};
